import { omit } from 'lodash/fp';

import { ReportTemplateConfigFormData } from 'dashboard/forms/FormReportTemplate';
import { ReportTemplateWatchlistConfigFormData } from 'dashboard/forms/FormReportTemplate/FormReportTemplateWatchlist';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  REPORT_BUILDER_NAME_TO_ENDPOINT,
  ReportTemplate,
  ReportTemplateWatchlistMeta,
} from 'dashboard/models/report-template';
import { ReportTemplateVersion } from 'dashboard/models/report-template-version';
import { setReportTemplate } from 'dashboard/redux/actions/report-template';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiOptions } from 'lib/clients/ApiClient';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export const loadReportTemplate =
  (reportTemplateId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const response = await SessionClientDashboard.getResource<
      ReportTemplate,
      ReportTemplateWatchlistMeta
    >(ApiEndpoint.ReportTemplate, reportTemplateId);
    const { data: reportTemplate, meta } = response;

    let latestPublishedVersion = null;
    if (reportTemplate.relationships?.['latest-published-version']) {
      const token = reportTemplate.relationships?.['latest-published-version'].data.id;
      latestPublishedVersion = response.included?.find((item) => item.id === token);
    }

    dispatch(
      setReportTemplate(reportTemplate, latestPublishedVersion as ReportTemplateVersion, meta),
    );
  };

export const createReportTemplate =
  (reportTemplateBuilderName: string): DashboardThunkAction<Promise<ReportTemplate>> =>
  async () => {
    const { data: reportTemplate } = await SessionClientDashboard.postResourceCreate<
      ReportTemplate,
      {
        type: ApiModel;
        attributes: { builder: string };
      }
    >({
      type: REPORT_BUILDER_NAME_TO_ENDPOINT[reportTemplateBuilderName],
      attributes: {
        builder: reportTemplateBuilderName,
      },
    });

    return reportTemplate;
  };

export const updateReportTemplate =
  (
    reportTemplateResource: ReportTemplate['type'],
    reportTemplateId: string,
    values: Partial<ReportTemplateConfigFormData>,
    options?: ApiOptions,
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    let attributes = values;
    if (reportTemplateResource === ApiModel.ReportTemplateWatchlist) {
      attributes = omit(
        ['source-category-types', 'archived', 'recurring-enabled', 'data-sources-persona'],
        attributes as ReportTemplateWatchlistConfigFormData,
      );
    }

    const response = await SessionClientDashboard.updateResource<ReportTemplate>(
      {
        id: reportTemplateId,
        type: reportTemplateResource,
        attributes: attributes,
      },
      options,
    );

    const { data: newReportTemplate } = response;
    let latestPublishedVersion = null;
    if (newReportTemplate.relationships?.['latest-published-version']) {
      const token = newReportTemplate.relationships?.['latest-published-version'].data.id;
      latestPublishedVersion = response.included?.find((item) => item.id === token);
    }

    dispatch(setReportTemplate(newReportTemplate, latestPublishedVersion as ReportTemplateVersion));
  };
